import React from 'react'
import { BrowserRouter } from 'react-router-dom'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { ThemeProvider as NextThemeProvider } from 'next-themes'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import { LanguageProvider } from '@/contexts/LanguageContext'
import { DropdownPortalProvider, ToastProvider, DebugOverlayProvider, TooltipProvider } from '@appic/ui-components'
import { FeatureFlagProvider } from '@appic/lib'
import { usePermissionMap } from '@appic/hooks'
import ServiceLayerAuthProvider from '@/lib/auth/ServiceLayerAuthProvider'
import { SessionExpiredOverlay } from '@/components/auth/SessionExpiredOverlay'
import { UserProvider } from '@/lib/auth/UserContext'
import { queryClient, idbPersister, CACHE_MAX_AGE } from '@appic/hooks'

/**
 * Persisted-query-cache buster. DELIBERATELY a stable literal — NOT the build
 * identity, which is what it used to read (`__BUILD_HASH__`, falling back to
 * 'dev').
 *
 * WHY IT MUST NOT TRACK THE BUILD: changing this value makes
 * PersistQueryClientProvider DISCARD every rep's persisted IndexedDB query cache
 * on the next load. A device fetches a new bundle while online, but the service
 * worker (registerType: 'autoUpdate') can activate it on a LATER load — which
 * may be offline, in the field. Busting there means an empty Tagesplanung for a
 * rep with no signal. Tying the buster to the build identity would do that on
 * every single deploy.
 *
 * The value stays 'dev' because that is what every live production bundle
 * already carries (the build hash resolved to the literal 'dev' in prod for
 * months — see buildIdentity.ts). Keeping it identical means fixing the build
 * identity busts NOBODY's cache. Bump it MANUALLY, and only when a cache-shape
 * change makes old persisted data unreadable.
 *
 * KNOWN GAP (reported, not silently fixed here): because prod's build hash was
 * a constant, this buster has never actually busted between prod deploys. That
 * is a real defect — it just is not this change's to make, since arming it has
 * the offline blast radius described above.
 */
const QUERY_CACHE_BUSTER = 'dev'

// CSP policy — enforcing mode (switched from report-only 2026-03-10, SS-236 I09)
const CSP_POLICY = [
  "default-src 'self'",
  "script-src 'self' 'unsafe-inline'",  // unsafe-inline needed for Vite dev + next-themes
  "style-src 'self' 'unsafe-inline'",   // Tailwind injects styles
  "img-src 'self' data: blob: https://*.supabase.co https://*.googleapis.com https://*.tile.openstreetmap.org",
  "font-src 'self'",
  "connect-src 'self' https://*.supabase.co wss://*.supabase.co https://*.googleapis.com https://*.sentry.io https://*.twilio.com wss://*.twilio.com https://*.twiliocdn.com https://*.clanda.de",
  "frame-src 'none'",
  "object-src 'none'",
  "base-uri 'self'",
].join('; ')

/** Wires RBAC permissions into feature flags — grants from user_permissions table */
function RbacFeatureFlags({ children }: { children: React.ReactNode }) {
  const { grantedFeatures, isLoading } = usePermissionMap()
  const features = grantedFeatures('scout', 'execute')
  return (
    <FeatureFlagProvider grantedFeatures={features} loading={isLoading}>
      {children}
    </FeatureFlagProvider>
  )
}

interface ProvidersProps {
  children: React.ReactNode
}

export const Providers: React.FC<ProvidersProps> = ({ children }) => {
  return (
    <HelmetProvider>
      <Helmet>
        <meta httpEquiv="Content-Security-Policy" content={CSP_POLICY} />
      </Helmet>
      <DebugOverlayProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister: idbPersister,
            // Never restore the persisted (IndexedDB) cache in dev: a stale
            // empty result cached during a transient outage otherwise masks a
            // now-working fetch (cost us hours chasing an "empty Tagesplanung"
            // that was really a cached blank). Prod keeps the persisted cache.
            maxAge: import.meta.env.DEV ? 0 : CACHE_MAX_AGE,
            buster: QUERY_CACHE_BUSTER,
          }}
        >
          <ServiceLayerAuthProvider>
            <UserProvider>
              <RbacFeatureFlags>
                <BrowserRouter>
                  <NextThemeProvider
                  attribute="class"
                  defaultTheme="system"
                  enableSystem
                  disableTransitionOnChange={false}
                  storageKey="appic-color-scheme"
                >
                  <LanguageProvider>
                    <DropdownPortalProvider>
                      <ToastProvider>
                        <TooltipProvider>
                          {children}
                          <SessionExpiredOverlay />
                        </TooltipProvider>
                      </ToastProvider>
                    </DropdownPortalProvider>
                  </LanguageProvider>
                </NextThemeProvider>
              </BrowserRouter>
              </RbacFeatureFlags>
            </UserProvider>
          </ServiceLayerAuthProvider>
        </PersistQueryClientProvider>
      </DebugOverlayProvider>
    </HelmetProvider>
  )
}
